import { Component, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UrlTree } from '@angular/router';
import { BaseButtonComponent } from '../base-button';
import { Format, hiddenFormats, Record, streamingFormats } from 'src/app/models/record';
import { CheckoutDownload } from '../../../models/checkout-download';
import { OleConfiguration } from '../../../models/ole-configuration';
import { BookClubCarousel } from '../../../models/bookclubcarousel';
import { ExperienceType } from '../../../models/experiences';
import { ExperienceLink } from '../../../models/experience-link';
import { ExperienceResult } from '../../../models/experience-result';
import { HistoryItem } from 'src/app/models/history-user';
import { Util } from 'src/app/util';
import { RecaptchaDialogComponent } from '../../../_shared/components/dialogs/recaptcha-dialog/recaptcha-dialog.component';
import { ExternalVideoModel } from 'src/app/_core/state/dialogs/external-video/model/external-video-data.model';
import { createWindowDeferred } from '../../../_core/utils/create-window-deferred';
import { AnalyticsService } from '../../../_core/analytics/analytics.service';

@Component({
  selector: 'app-download-button',
  templateUrl: './download-button.component.html',
  styleUrls: ['./download-button.component.scss', '../base-button.scss'],
})
export class DownloadButtonComponent extends BaseButtonComponent implements OnInit, OnChanges {
  private readonly newAnalyticsService = inject(AnalyticsService);

  @Input()
  isMoreOption: string;
  @Input()
  certificateItem: boolean;
  @Input()
  historyItem: HistoryItem;
  @ViewChild(RecaptchaDialogComponent) recaptchaDialog!: RecaptchaDialogComponent;

  formats: Format[] = [];
  defaultFormat: Format;
  buttonLabel: string;
  textDirection: string;

  readonly STREAMING_FORMATS: Format[] = streamingFormats;
  readonly HIDDEN_FORMATS: Format[] = hiddenFormats;
  private MAX_BOOK_CLUBS = 300;
  private ONLY_MODERATES = true;
  readonly ALLOWED_BOOKCLUBMODE_FORMATS: string[] = ['EPUB', 'PDF'];

  private downloadFormat: string;

  ngOnInit() {
    this.textDirection = this.localeService.getTextDirection();
    this.record.formats.forEach((format) => {
      if (!this.defaultFormat && ![...this.HIDDEN_FORMATS].includes(format)) {
        this.defaultFormat = format;

        if (['MP3_LH', 'ACSM'].includes(format)) {
          this.buttonLabel = 'common.download';
        } else if (this.STREAMING_FORMATS.includes(format) && !this.certificateItem) {
          this.buttonLabel = 'button.play';
        } else if (this.certificateItem) {
          this.buttonLabel = 'button.certificate';
        } else if (format === 'HTML') {
          this.buttonLabel = 'button.read';
        } else {
          this.buttonLabel = 'button.read';
        }
      }
    });

    this.filterFormats();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.record) {
      delete this.defaultFormat;
      this.ngOnInit();
    }
  }

  filterFormats() {
    this.formats = this.record.formats.filter((format) => {
      if (format === 'ACSM') {
        if (!this.configService.get().showAcsmButton) {
          return false;
        }
      }
      return (
        (!this.HIDDEN_FORMATS.includes(format) && format !== this.defaultFormat) ||
        (format === 'SCORM' && this.certificateItem)
      );
    });
  }

  getActionLabel(format: Format): string {
    switch (format) {
      case 'EBOOK_STREAMING':
      case 'PDF':
      case 'EPUB':
        return 'button.actions.nubereader';
      case 'ACSM':
        return 'button.actions.acsm';
      case 'MP3':
      case 'MP4':
        return 'button.actions.nubeplayer';
      default:
        return 'common.open';
    }
  }

  download(format?: string, accessMode: 'downloaded' | 'streaming' = 'streaming') {
    this.newAnalyticsService.sendContentViewedEvent(
      this.record,
      this.record.freeDownload ? 'free_access' : 'checkout',
      accessMode,
      { isRssItem: this.rssItem != null }
    );

    this.downloadFormat = format || this.defaultFormat;

    if (!this.record.container) {
      this.rhId = null;
    }

    if (this.rssItem != null) {
      this.rhId = this.rssItem.rhId;
    }

    if (this.historyItem?.urlCertificate) {
      this.downloadCertificate();
      return;
    }

    // Check recaptcha
    if (this.configService.get().enableDrmCaptcha && this.downloadFormat === 'ACSM') {
      this.recaptchaDialog.openDialog();
      return;
    }

    this.internalDownload();
  }

  // Should show the modal if there are any experiences, and the bookclub mode is allowed
  private internalDownload(captchaToken?: string) {
    const checkout = this.checkoutsFacade.getCheckoutByRecordId(this.record.id, this.issueDate, this.rhId);
    if (!checkout) {
      return;
    }
    const token = this.extractToken(checkout.downloadUrl);
    const userId = this.userService.getUserId();

    this.apiService.getLearningExperiencesAssociatedToRecordId(this.record.id, userId).subscribe((exps) => {
      const learningExperienceId = Util.extractLearningExperienceId(exps);
      this.apiService
        .downloadResource(
          checkout.id,
          userId,
          this.downloadFormat,
          token,
          this.record.id,
          learningExperienceId,
          captchaToken
        )
        .subscribe(
          (response) => {
            if (this.shouldShowTheModal(exps, this.downloadFormat)) {
              this.apiService.getOleConfiguration(userId).subscribe((oleConfig) => {
                this.apiService.getBookClubs(userId, this.MAX_BOOK_CLUBS, this.ONLY_MODERATES).subscribe({
                  next: (bookClubsCollection) => {
                    this.handleLearningExperiences(exps, response, oleConfig, userId, bookClubsCollection);
                  },
                  error: () => {
                    this.handleLearningExperiences(exps, response, oleConfig, userId, []);
                  },
                });
              });
            } else {
              if (this.downloadFormat.includes('ACSM')) {
                this.checkoutsFacade.checkouts.find((_checkout) => _checkout.id === checkout.id).returnable = false;
              }

              if (this.downloadFormat.includes('HTML')) {
                return this.openDialog(response.redirect);
              }

              if (typeof response.redirect === 'string') {
                if (this.downloadFormat === 'SCORM') {
                  const backUrl = new URL(location.href);
                  backUrl.searchParams.set('showReturnButton', 'false');

                  response.redirect += `?backUrl=${encodeURIComponent(backUrl.toString())}`;
                }

                location.href = response.redirect;

                if (this.downloadFormat.includes('MP3_LH')) {
                  this.toastService.showToast('messages.checkouts.download', 'info');
                }
              }
            }
          },
          (err) => {
            throw err;
          }
        );
    });
  }
  // and the element is either and epub or a pdf. Don't show if it's ACSM.
  private shouldShowTheModal(exps: Record[], format: string) {
    if (format && format.toUpperCase() === 'ACSM') {
      return false;
    }
    return (
      exps &&
      this.configService.get().showBookclubMode &&
      this.record.fileFormat &&
      this.ALLOWED_BOOKCLUBMODE_FORMATS.includes(this.record.fileFormat.toUpperCase())
    );
  }

  private handleLearningExperiences(
    exps: Record[],
    response: CheckoutDownload,
    oleConfig: OleConfiguration,
    userId: string,
    bookClubCollections: BookClubCarousel[]
  ) {
    const miniExps: ExperienceResult[] = exps.map((exp) => ({
      learningExperienceId: exp.learningExperienceId,
      title: exp.title,
      externalUrl: new URL(exp.externalLink).origin,
    }));
    miniExps.sort((first, second) => first.title.localeCompare(second.title));
    const links: ExperienceLink[] = this.getExperienceLinks(response, miniExps, oleConfig, bookClubCollections);
    if (links.length === 1) {
      const exp = links[0];
      location.href = exp.url;
    } else {
      this.bookClubHistoryService
        .getBookClubHistoryByUserIdAndResourceId(userId, this.getRecordId())
        .subscribe((bookClubHistory) => {
          if (bookClubHistory.some((history) => history.dontShow === true) || response.dontShowModal) {
            let experienceId = bookClubHistory.length
              ? bookClubHistory[bookClubHistory.length - 1].learningExperienceId
              : '';
            if (experienceId === 'plain') {
              experienceId = '';
            }
            const link: ExperienceLink = links.find((element) => element.learningExperienceId === experienceId);
            location.href = link.url;
          } else {
            this.bookclubLinksDialogFacade.openDialog(this.record, links, this.getRecordId());
          }
        });
    }
  }

  private getExperienceLinks(
    response: CheckoutDownload,
    experiencesArray: ExperienceResult[],
    oleConfig: OleConfiguration,
    bookClubCollections: BookClubCarousel[]
  ) {
    const backUrl = new URL(location.href);
    backUrl.searchParams.set('showReturnButton', 'false');

    const moderateArray: BookClubCarousel[] = bookClubCollections.filter((bookClub) => bookClub.name === 'moderates');
    const links: ExperienceLink[] = [
      ...experiencesArray.map((exp) => {
        const url = new URL(response.redirect as string);
        url.searchParams.set('bc', exp.externalUrl);
        url.searchParams.set('c', exp.learningExperienceId);
        // searchParams encodes on its own; encoding here too would send the value encoded twice.
        url.searchParams.set('backUrl', backUrl.toString());

        return {
          name: exp.title,
          url: url.toString(),
          learningExperienceId: exp.learningExperienceId,
          clicked: false,
          type: this.typeOfExperience(moderateArray, exp),
        };
      }),
    ];
    if (this.isNormalUser(oleConfig, experiencesArray) || this.isCollaboratorUser(oleConfig, experiencesArray)) {
      return links;
    }
    if (experiencesArray.length > 1) {
      const url = new URL(response.redirect as string);
      url.searchParams.set('backUrl', backUrl.toString());

      links.push({
        name: this.record.title,
        url: url.toString(),
        learningExperienceId: '',
        clicked: false,
        type: ExperienceType.PLAIN,
      });
    }
    return links;
  }

  private getRecordId() {
    if (this.availability === undefined) {
      return this.record.id;
    }
    return this.availability.recordId;
  }

  private typeOfExperience(
    moderateArray: BookClubCarousel[],
    exp: { externalUrl: string; learningExperienceId: string; title: string }
  ): ExperienceType {
    if (moderateArray.length === 0) {
      return ExperienceType.FOLLOWING;
    }
    const namesOfExperiences = moderateArray[0].bookClubs.map((experiences) => experiences.name);
    if (namesOfExperiences.some((name) => name === exp.title)) {
      return ExperienceType.MODERATES;
    }
    return ExperienceType.FOLLOWING;
  }

  private isNormalUser(
    oleConfig: OleConfiguration,
    experiencesArray: {
      externalUrl: string;
      learningExperienceId: string;
      title: string;
    }[]
  ) {
    return (
      oleConfig.canCreateExperiences === false &&
      oleConfig.canCreateUserGroups === false &&
      experiencesArray.length === 1
    );
  }

  private isCollaboratorUser(
    oleConfig: OleConfiguration,
    experiencesArray: {
      externalUrl: string;
      learningExperienceId: string;
      title: string;
    }[]
  ) {
    return (
      oleConfig.canCreateExperiences === false &&
      oleConfig.canCreateUserGroups === true &&
      experiencesArray.length === 1
    );
  }

  private openDialog(link) {
    if (!this.record.container) {
      this.rhId = null;
    }

    if (this.rssItem != null) {
      this.rhId = this.rssItem.rhId;
    }

    const externalVideoModel: ExternalVideoModel = {
      title: this.record.title,
      link: link,
      format: this.record.formats,
      userId: this.userService.getUserId(),
      resourceId: this.record.id,
      isFromExternalDb: false,
      checkoutId: this.checkoutsFacade.getCheckoutByRecordId(this.record.id, this.issueDate, this.rhId).id,
    };

    this.externalVideoDialogFacade.openDialog(externalVideoModel);
  }

  private extractToken(url: string): string {
    const downloadUrl = url.replace(/^(https?:|)\/\//, '');
    const parsedUrl: UrlTree = this.router.parseUrl(downloadUrl);

    return parsedUrl.queryParams.token;
  }

  public downloadCertificate() {
    const [openWindow] = createWindowDeferred();
    this.apiService.downloadCertificate(this.historyItem.urlCertificate).subscribe((downloadUrl) => {
      openWindow(downloadUrl);
    });
  }

  public shareCertificateOnLinkedin() {
    let url;
    const linkedinEndpoint = 'https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME';
    const andParam = '&';
    const paramNameKey = 'name=';
    const paramOrganizationIdKey = 'organizationId=';
    const paramIssueMonthKey = 'issueMonth=';
    const paramIssueYearKey = 'issueYear=';
    const paramCertUrlKey = 'certUrl=';
    const paramOrganizationIdValue = this.configService.getCertificationEntityId();

    const match = this.historyItem.urlCertificate.match(/_(\d+)\.pdf/);
    const date = match[1];
    const paramIssueYearValue = date.slice(-4);
    const paramIssueMonthValue = date.slice(-6, -4);

    const paramCertUrlValue =
      this.apiService.API_URL + '/records/download-certificate' + '?path=' + this.historyItem.urlCertificate;

    url =
      linkedinEndpoint +
      andParam +
      paramNameKey +
      this.record.title +
      andParam +
      paramOrganizationIdKey +
      paramOrganizationIdValue +
      andParam +
      paramIssueMonthKey +
      paramIssueMonthValue +
      andParam +
      paramIssueYearKey +
      paramIssueYearValue +
      andParam +
      paramCertUrlKey +
      encodeURIComponent(paramCertUrlValue);

    window.open(url);
  }

  resolvedCaptcha(token: string) {
    if (token) {
      this.recaptchaDialog.closeDialog();
      this.internalDownload(token);
    }
  }
}
